import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Ticket, Plus, Printer, Eye, Filter, Download } from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

export default function TicketsPage() {
  const { getAuthHeader } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [ticketType, setTicketType] = useState('physical');
  const [submitting, setSubmitting] = useState(false);
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const printRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tickets`, getAuthHeader()),
        axios.get(`${API_URL}/api/events`, getAuthHeader())
      ]);
      setTickets(ticketsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }
    setSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/api/tickets/generate`, {
        event_id: selectedEvent,
        quantity: parseInt(quantity),
        ticket_type: ticketType
      }, getAuthHeader());
      
      toast.success(`${response.data.length} tickets generated successfully`);
      fetchData();
      setGenerateDialogOpen(false);
      setSelectedEvent('');
      setQuantity('1');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate tickets');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setViewDialogOpen(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    // Sanitize ticket ID to prevent XSS
    const safeTicketId = selectedTicket.ticket_id.replace(/[<>"'&]/g, '');
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print tickets');
      return;
    }
    
    // Create document using DOM methods instead of document.write
    const doc = printWindow.document;
    doc.open();
    doc.write('<!DOCTYPE html><html><head>');
    doc.write(`<title>Ticket - ${safeTicketId}</title>`);
    doc.write('<link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap" rel="stylesheet">');
    doc.write(`<style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'JetBrains Mono', monospace; background: white; }
      .ticket { width: 300px; padding: 15px; margin: 0 auto; }
      .divider { text-align: center; font-size: 9px; letter-spacing: 2px; color: #999; margin: 8px 0; }
      .header { text-align: center; padding: 10px 0; }
      .header h1 { font-size: 18px; font-weight: bold; letter-spacing: 4px; }
      .section { margin: 10px 0; }
      .section-label { font-size: 9px; text-transform: uppercase; color: #666; }
      .section-value { font-size: 14px; font-weight: bold; margin-top: 2px; }
      .ticket-id { text-align: center; font-size: 14px; font-weight: bold; letter-spacing: 2px; padding: 8px 0; }
      .qr-section { text-align: center; padding: 15px 0; }
      .qr-section img, .qr-section svg { width: 160px; height: 160px; }
      .price { text-align: center; font-size: 20px; font-weight: bold; padding: 10px 0; }
      .footer { text-align: center; font-size: 9px; color: #666; padding-top: 10px; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style>`);
    doc.write('</head><body>');
    doc.write(printContent.innerHTML);
    doc.write('</body></html>');
    doc.close();
    
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const copyTicketLink = (ticketId) => {
    const link = `${FRONTEND_URL}/ticket/${ticketId}`;
    navigator.clipboard.writeText(link);
    toast.success('Ticket link copied to clipboard');
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filterEvent !== 'all' && ticket.event_id !== filterEvent) return false;
    if (filterStatus !== 'all' && ticket.status !== filterStatus) return false;
    return true;
  });

  return (
    <Layout title="Tickets">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <p className="text-muted-foreground">Generate and manage tickets</p>
        <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="generate-tickets-button">
              <Plus className="w-4 h-4 mr-2" />
              Generate Tickets
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">Generate Tickets</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleGenerate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide font-medium">Select Event</Label>
                <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                  <SelectTrigger data-testid="select-event-for-tickets">
                    <SelectValue placeholder="Choose an event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.name} - {event.date}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide font-medium">Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  data-testid="ticket-quantity-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide font-medium">Ticket Type</Label>
                <Select value={ticketType} onValueChange={setTicketType}>
                  <SelectTrigger data-testid="ticket-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">Physical (Printed)</SelectItem>
                    <SelectItem value="digital">Digital (Mobile)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setGenerateDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || !selectedEvent} 
                  className="flex-1 bg-[#0055FF] hover:bg-[#0044CC]"
                  data-testid="confirm-generate-button"
                >
                  {submitting ? 'Generating...' : 'Generate'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border border-border bg-white mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <div className="flex flex-wrap gap-4">
              <Select value={filterEvent} onValueChange={setFilterEvent}>
                <SelectTrigger className="w-[200px]" data-testid="filter-event">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]" data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="UNUSED">Unused</SelectItem>
                  <SelectItem value="USED">Used</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card className="border border-border bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="py-12 text-center">
              <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No tickets found. Generate some tickets!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold text-xs uppercase tracking-wide">Ticket ID</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide">Event</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide">Date</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide">Type</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide">Status</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide">Price</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket.id} data-testid={`ticket-row-${ticket.ticket_id}`}>
                      <TableCell className="font-mono font-semibold">{ticket.ticket_id}</TableCell>
                      <TableCell>{ticket.event_name}</TableCell>
                      <TableCell>{ticket.date}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs uppercase">
                          {ticket.ticket_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={ticket.status === 'UNUSED' 
                            ? 'bg-[#00C853] hover:bg-[#00C853] text-white' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-200'
                          }
                        >
                          {ticket.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold">D{ticket.price}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewTicket(ticket)}
                            data-testid={`view-ticket-${ticket.ticket_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyTicketLink(ticket.ticket_id)}
                            data-testid={`copy-link-${ticket.ticket_id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View/Print Ticket Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Ticket Preview</DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <>
              {/* Printable Ticket - Gam Ticket Design */}
              <div ref={printRef} className="thermal-ticket mx-auto border border-border p-4 font-mono bg-white">
                <div className="ticket">
                  {/* Header */}
                  <div className="text-center text-xs text-gray-400 tracking-widest">============================</div>
                  <div className="text-center py-2">
                    <h1 className="text-lg font-bold tracking-widest uppercase">GAM TICKET</h1>
                  </div>
                  <div className="text-center text-xs text-gray-400 tracking-widest mb-4">============================</div>

                  {/* Event Details */}
                  <div className="space-y-3 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">EVENT:</p>
                      <p className="font-bold text-lg">{selectedTicket.event_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">DATE:</p>
                      <p className="font-bold">{selectedTicket.date}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">VENUE:</p>
                      <p className="font-bold">{selectedTicket.venue}</p>
                    </div>
                  </div>

                  {/* Ticket ID */}
                  <div className="text-center text-xs text-gray-400 tracking-widest">----------------------------</div>
                  <div className="text-center py-2">
                    <p className="font-bold tracking-widest">TICKET ID: {selectedTicket.ticket_id}</p>
                  </div>
                  <div className="text-center text-xs text-gray-400 tracking-widest">----------------------------</div>

                  {/* QR Code Section */}
                  <div className="py-4 flex justify-center">
                    <div className="p-3 border-2 border-dashed border-gray-300">
                      <QRCode 
                        value={selectedTicket.ticket_id} 
                        size={160}
                        level="H"
                        fgColor="#000000"
                        bgColor="#FFFFFF"
                      />
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-center text-xs text-gray-400 tracking-widest">----------------------------</div>
                  <div className="text-center py-3">
                    <p className="text-2xl font-bold">PRICE: D{selectedTicket.price}</p>
                  </div>
                  <div className="text-center text-xs text-gray-400 tracking-widest">----------------------------</div>

                  {/* Footer */}
                  <div className="text-center pt-3 mt-2">
                    <p className="text-xs text-gray-500">Powered by <span className="font-bold text-black">Gam Ticket</span></p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => copyTicketLink(selectedTicket.ticket_id)}
                  className="flex-1"
                  data-testid="copy-ticket-link"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={handlePrint}
                  className="flex-1 bg-[#0055FF] hover:bg-[#0044CC]"
                  data-testid="print-ticket-button"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Ticket
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
